import { Fragment, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Popover, PopoverOrigin, Stack } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import cn from 'classnames';

import { navConfig } from 'layouts/dashboard/navbar/NavConfig';
import { setCurrentPath } from 'utils/setCurrentPath';
import { NavConfig, NavListProps } from './type';
import { UserValues } from 'types/user';
import { AuthUser } from 'types/auth';
import { Typography } from 'components/Typography/Typography';
import { useCollapseDrawer } from 'hooks/useCollapseDrawer';
import { useAuth } from 'hooks/useAuth';

import s from './styles.module.scss';

const hasRole = (user: AuthUser, roles: UserValues[]) => {
  const userRoles = user ? user.roles : [{ name: 'guest' }];
  const rolesArray = userRoles.map((role) => role.name);

  return roles.some((role) => rolesArray.includes(role));
};

const itemsContainsRoles = (user: AuthUser, item: NavListProps) => {
  if (item.items !== undefined) {
    return item.items.some((subitem) => hasRole(user, subitem.roles));
  } else {
    return hasRole(user, item.roles);
  }
};

interface ItemPopoverProps {
  subheader: string;
  item: NavListProps;
}

const ItemPopover = ({ subheader, item }: ItemPopoverProps) => {
  const navigate = useNavigate();

  const { opened, hovered, setHovered, items, path, setItems, setPath } = useCollapseDrawer();
  const { user } = useAuth();

  const origin = {
    vertical: 'top',
    horizontal: 'right',
  } as PopoverOrigin;

  const handleClose = () => {
    setItems({
      ...items,
      [item.title]: null,
    });

    if (!opened && hovered) {
      setHovered(false);
    }
  };

  const handleClick = (item: NavListProps, subitem: NavListProps) => {
    if (!subitem.path) return;

    handleClose();

    setPath({ tab: subheader, item: item.title, subitem: subitem.title });

    navigate(subitem.path);
  };

  const filteredItems = item?.items?.filter((subitem) => hasRole(user, subitem.roles));

  return (
    <Popover
      id={Boolean(items[item.title]) ? `popover-${item.title}` : undefined}
      anchorEl={items[item.title]}
      open={Boolean(items[item.title])}
      anchorOrigin={origin}
      onClose={handleClose}
    >
      <Stack className={s.popover}>
        {filteredItems &&
          filteredItems.map((subitem) => (
            <Button
              key={subitem.title}
              onClick={() => handleClick(item, subitem)}
              sx={
                path.subitem === subitem.title
                  ? {
                      '& p': { color: '#1976D2' },
                    }
                  : undefined
              }
            >
              <Typography fontSize="14px">{subitem.title}</Typography>
            </Button>
          ))}
      </Stack>
    </Popover>
  );
};

interface ItemsProps {
  subheader: string;
  tabItems: NavListProps[];
  isOpen: boolean;
}

const Items = ({ subheader, tabItems, isOpen }: ItemsProps) => {
  const navigate = useNavigate();

  const { opened, hovered, setHovered, items, setItems, path, setPath } = useCollapseDrawer();
  const { user } = useAuth();

  const handleClassName = (item: NavListProps) =>
    cn(s.subTabButton, {
      [items.hasOwnProperty(item.title) ? s.subTabButtonPopover : s.subTabButtonOpen]: isOpen,
      [s.closed]: !opened,
      [s.hovered]: !opened && hovered,
    });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, item: NavListProps) => {
    if (item.path) {
      setPath({ tab: subheader, item: item.title, subitem: item.path });

      navigate(item.path);

      return;
    }

    if (item.items) {
      setItems({
        ...items,
        [item.title]: e.currentTarget,
      });

      if (!opened && !hovered) {
        setHovered(true);
      }

      return;
    }
  };

  const filteredItems = tabItems.filter((item) => itemsContainsRoles(user, item));

  return (
    <>
      {filteredItems.map((item) => (
        <Fragment key={item.title}>
          <Stack
            component="button"
            aria-describedby={item?.items ? `popover-${item.title}` : undefined}
            className={handleClassName(item)}
            onClick={(e) => handleClick(e, item)}
            sx={
              path.item === item.title
                ? {
                    '& p': { color: '#1976D2' },
                  }
                : undefined
            }
          >
            <Typography fontSize="0.875rem" lineHeight="20px">
              {item.title}
            </Typography>
          </Stack>
          {items.hasOwnProperty(item.title) && <ItemPopover subheader={subheader} item={item} />}
        </Fragment>
      ))}
    </>
  );
};

export const NavMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { opened, hovered, tabs, path, setTabs, setPath } = useCollapseDrawer();
  const { user } = useAuth();

  useEffect(() => {
    const currentPath = location.pathname;

    setCurrentPath(currentPath, navConfig, tabs, setPath, setTabs);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleClick = (tab: NavConfig, length: number | undefined) => {
    const path = tab?.item?.path;

    if (path) {
      setPath({ tab: tab.subheader, item: null, subitem: null });

      navigate(path);

      return;
    }

    if (length) {
      setTabs({
        ...tabs,
        [tab.subheader]: !tabs[tab.subheader],
      });

      return;
    }
  };

  const config = navConfig.filter((tab) => {
    if (tab.items !== undefined) {
      return tab.items.some((item) => itemsContainsRoles(user, item));
    } else if (tab.item !== undefined) {
      return hasRole(user, tab.item.roles);
    } else {
      return false;
    }
  });

  return (
    <div className={s.scroll}>
      {config.map((tab) => {
        const length = tab?.items?.length;
        const isOpen = tabs[tab.subheader];

        return (
          <Fragment key={tab.subheader}>
            <Stack
              className={cn(s.tabButton, { [s.closed]: !opened, [s.hovered]: !opened && hovered })}
              component={length ? 'button' : 'a'}
              onClick={() => handleClick(tab, length)}
              sx={
                path.tab === tab.subheader
                  ? {
                      '& p': { color: '#1976D2' },
                      '& path': { fill: '#1976D2', fillOpacity: 0.87 },
                    }
                  : undefined
              }
            >
              {tab.icon}
              <Typography fontSize="1rem" lineHeight="24px">
                {tab.subheader}
              </Typography>
              {length && <ArrowBackIosIcon className={cn(s.arrow, { [s.arrowUp]: isOpen })} />}
            </Stack>
            {tab.items && <Items subheader={tab.subheader} tabItems={tab.items} isOpen={isOpen} />}
          </Fragment>
        );
      })}
    </div>
  );
};
